import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { Button } from '../../components/button/button';
import { CoordinateSystemSelector } from '../../components/coordinate-system-selector/coordinate-system-selector';
import { Dialog } from '../../components/dialog/dialog';
import { DropdownSuggestion } from '../../components/dropdown-suggestion/dropdown-suggesion';
import { InfoBox } from '../../components/info-box/info-box';
import { LabelledContainer } from '../../components/labelled-container/labelled-container';
import { Stack } from '../../components/stack/stack';
import { TextBox } from '../../components/text-box/text-box';
import { TooltipInfoIcon } from '../../components/tooltip-info-icon/tooltip-info-icon';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useToaster } from '../../state/toaster-state';

type CompanyOption = {
  id: string;
  name: string | null;
};

type Props = {
  companies: CompanyOption[];
  disabled?: boolean;
  onCreated?: () => Promise<void> | void;
};

type CreateProjectVariables = {
  name: string;
  companyId: string;
};

type UpdateSpatialRefVariables = {
  projectId: string;
  coordinateSystemId: string | null;
  geoidId: string | null;
};

export const CreateProjectButton = ({ companies, disabled = false, onCreated }: Props) => {
  const { t } = useTranslation();
  const buttonLabel = t('projects.create.button', { ns: 'companies' });
  const [open, setOpen] = React.useState(false);

  const buttonDisabled = disabled || companies.length === 0;

  return (
    <>
      <Button
        color="primary"
        disabled={buttonDisabled}
        leftIcon={{ icon: 'plus' }}
        variant="contained"
        onClick={() => {
          if (buttonDisabled) {
            return;
          }
          setOpen(true);
        }}
      >
        {buttonLabel}
      </Button>

      {open ? (
        <CreateProjectDialog
          companies={companies}
          onClose={() => setOpen(false)}
          onCreated={onCreated}
        />
      ) : null}
    </>
  );
};

type DialogProps = {
  companies: CompanyOption[];
  onClose: () => void;
  onCreated?: () => Promise<void> | void;
};

// TODO: fetch the companies from the upcoming companies state.
const CreateProjectDialog = ({ companies, onClose, onCreated }: DialogProps) => {
  const { t } = useTranslation();
  const createErrorHandling = useErrorHandling();
  const updateCsErrorHandling = useErrorHandling();
  const { showToast } = useToaster();

  const nameInputId = React.useId();
  const companySelectId = React.useId();

  const [projectName, setProjectName] = React.useState('');
  const [selectedCompanyId, setSelectedCompanyId] = React.useState('');
  const [showValidation, setShowValidation] = React.useState(false);
  const [selectedCsId, setSelectedCsId] = React.useState<string | null>(null);
  const [selectedGeoidId, setSelectedGeoidId] = React.useState<string | null>(null);
  const [localCoordinatesConfirmed, setLocalCoordinatesConfirmed] = React.useState(false);
  const [projectType, setProjectType] = React.useState<'georeferenced' | 'local' | null>(null);

  // Stored after the project is created so the update step can be retried independently.
  const createdProjectRef = React.useRef<{ id: string; name: string } | null>(null);

  const resetForm = React.useCallback(() => {
    setProjectName('');
    setSelectedCompanyId('');
    setShowValidation(false);
    setSelectedCsId(null);
    setSelectedGeoidId(null);
    setLocalCoordinatesConfirmed(false);
    setProjectType(null);
    createdProjectRef.current = null;
    createErrorHandling.clearErrors();
    updateCsErrorHandling.clearErrors();
  }, [createErrorHandling, updateCsErrorHandling]);

  const companiesOptions = React.useMemo(
    () =>
      companies
        .filter((company) => typeof company.id === 'string' && company.id.length > 0)
        .map((company) => ({
          id: company.id,
          name: company.name ?? t('untitled', { ns: 'common' }),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [companies, t],
  );

  React.useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }

    if (!companiesOptions.some((company) => company.id === selectedCompanyId)) {
      setSelectedCompanyId('');
    }
  }, [companiesOptions, selectedCompanyId]);

  const createMutation = useMutation({
    mutationFn: async (variables: CreateProjectVariables) => {
      const service = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
      return service.create({
        body: {
          project: {
            name: variables.name,
            companyId: variables.companyId,
          },
        },
      });
    },
    networkMode: 'always',
    retry: false,
  });

  const updateSpatialRefMutation = useMutation({
    mutationFn: async (variables: UpdateSpatialRefVariables) => {
      const service = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      if (variables.coordinateSystemId) {
        await service.putCoordinateSystem({
          path: { projectId: variables.projectId },
          body: { id: variables.coordinateSystemId },
        });
      }

      if (variables.geoidId) {
        await service.putGeoid({
          path: { projectId: variables.projectId },
          body: { id: variables.geoidId },
        });
      }
    },
    networkMode: 'always',
    retry: false,
  });

  const isPending = createMutation.isPending || updateSpatialRefMutation.isPending;
  const createButtonDisabled = isPending || (!selectedCsId && !localCoordinatesConfirmed);

  const handleFullSuccess = React.useCallback(
    async (name: string) => {
      showToast({
        type: 'success',
        title: t('toaster.title.success', { ns: 'common' }),
        message: t('projects.create.toaster.success', { ns: 'companies', name }),
      });

      if (onCreated) {
        await onCreated();
      }

      resetForm();
      onClose();
    },
    [showToast, t, onCreated, resetForm, onClose],
  );

  const runUpdateStep = React.useCallback(
    async (projectId: string, csId: string | null, geoidId: string | null) => {
      try {
        await updateSpatialRefMutation.mutateAsync({
          projectId,
          coordinateSystemId: csId,
          geoidId,
        });
        await handleFullSuccess(createdProjectRef.current?.name ?? '');
      } catch (error) {
        updateCsErrorHandling.handleError(error, {
          retryCallback: () => void runUpdateStep(projectId, csId, geoidId),
        });
      }
    },
    [updateSpatialRefMutation, handleFullSuccess, updateCsErrorHandling],
  );

  const onSubmit = React.useCallback(async () => {
    setShowValidation(true);

    if (projectName.trim().length === 0 || !selectedCompanyId) {
      return;
    }

    createErrorHandling.clearErrors();
    updateCsErrorHandling.clearErrors();

    // Project already created but update failed — skip re-creation.
    if (createdProjectRef.current) {
      await runUpdateStep(createdProjectRef.current.id, selectedCsId, selectedGeoidId);
      return;
    }

    const name = projectName.trim();

    try {
      const result = await createMutation.mutateAsync({ name, companyId: selectedCompanyId });
      const projectId = result.data.id;
      createdProjectRef.current = { id: projectId, name };

      if (!localCoordinatesConfirmed && (selectedCsId || selectedGeoidId)) {
        await runUpdateStep(projectId, selectedCsId, selectedGeoidId);
      } else {
        await handleFullSuccess(name);
      }
    } catch (error) {
      createErrorHandling.handleError(error, { retryCallback: () => void onSubmit() });
    }
  }, [
    projectName,
    selectedCompanyId,
    selectedCsId,
    selectedGeoidId,
    localCoordinatesConfirmed,
    createMutation,
    createErrorHandling,
    updateCsErrorHandling,
    runUpdateStep,
    handleFullSuccess,
  ]);

  const closeDialog = React.useCallback(() => {
    if (isPending) {
      return;
    }
    resetForm();
    onClose();
  }, [isPending, onClose, resetForm]);

  const nameError =
    showValidation && projectName.trim().length === 0
      ? t('projects.create.validation.nameRequired', { ns: 'companies' })
      : undefined;

  const companyError =
    showValidation && !selectedCompanyId
      ? t('projects.create.validation.companyRequired', { ns: 'companies' })
      : undefined;

  const createErrorList = createErrorHandling.buildErrorList();
  const updateCsErrorList = updateCsErrorHandling.buildErrorList();

  return (
    <Dialog
      closeIcon={false}
      closeOnDimmerClick={false}
      height="expand"
      maxWidth={500}
      width={500}
      onClose={closeDialog}
    >
      {{
        header: t('projects.create.dialogTitle', { ns: 'companies' }),
        content: (
          <Stack spacing={1.5}>
            <LabelledContainer text={t('name', { ns: 'common' })}>
              <TextBox
                autoFocus={true}
                disabled={isPending}
                errorMessage={nameError}
                id={nameInputId}
                placeholder={t('projects.create.namePlaceholder', { ns: 'companies' })}
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
              />
            </LabelledContainer>

            <LabelledContainer text={t('title', { ns: 'companies' })}>
              <DropdownSuggestion
                disabled={isPending || companiesOptions.length === 0}
                id={companySelectId}
                placeholder={t('projects.create.companyPlaceholder', { ns: 'companies' })}
                portalToBody={true}
                suggestions={companiesOptions}
                value={selectedCompanyId || undefined}
                width="100%"
                onClear={() => setSelectedCompanyId('')}
                onSuggestionSelected={(suggestion) => setSelectedCompanyId(suggestion.id)}
              />
              {companyError && (
                <InfoBox color="red" fontSize="small" padding="small">
                  {companyError}
                </InfoBox>
              )}
            </LabelledContainer>

            <LabelledContainer text={t('projectSettings.projection.title', { ns: 'components' })}>
              <Stack direction="column" spacing={0.5}>
                <RadioOption>
                  <input
                    checked={projectType === 'georeferenced'}
                    disabled={isPending}
                    type="radio"
                    onChange={() => {
                      setProjectType('georeferenced');
                      setLocalCoordinatesConfirmed(false);
                    }}
                  />
                  {t('projects.create.georeferenced', { ns: 'companies' })}
                </RadioOption>
                <RadioOption>
                  <input
                    checked={projectType === 'local'}
                    disabled={isPending}
                    type="radio"
                    onChange={() => {
                      setProjectType('local');
                      setLocalCoordinatesConfirmed(true);
                      setSelectedCsId(null);
                      setSelectedGeoidId(null);
                    }}
                  />
                  {t('projects.create.local', { ns: 'companies' })}
                  <TooltipInfoIcon
                    content={
                      <Trans
                        components={{ br: <br /> }}
                        i18nKey="projects.create.localCoordinatesTooltip"
                        ns="companies"
                      />
                    }
                    title={t('projects.create.localCoordinatesTooltipTitle', { ns: 'companies' })}
                  />
                </RadioOption>
                {projectType === 'georeferenced' && (
                  <CoordinateSystemSelector
                    disabled={isPending}
                    onCoordinateSystemChange={setSelectedCsId}
                    onGeoidChange={setSelectedGeoidId}
                  />
                )}
              </Stack>
            </LabelledContainer>

            {createErrorList && <Stack spacing={0.25}>{createErrorList}</Stack>}

            {updateCsErrorList && <Stack spacing={0.25}>{updateCsErrorList}</Stack>}
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={createButtonDisabled}
                loading={isPending}
                variant="contained"
                onClick={() => void onSubmit()}
              >
                {t('create', { ns: 'common' })}
              </Button>
              <Button disabled={isPending} variant="text" onClick={closeDialog}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
`;
